import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import type { MediaPlayer } from '../Timeline/useMediaPlayer';
import { markOnDark, studioDark } from './studioTokens';

export interface StageNote {
  id: string;
  label: string;
  text: string;
  color: string;
}

interface VideoStageProps {
  videoUrl?: string;
  player: MediaPlayer;
  testId?: string;
  /** Notes that apply at the playhead, shown over the picture. */
  notes?: StageNote[];
  /** Shown when there is no video URL. */
  placeholder?: React.ReactNode;
  /** CSS max-height of the picture when not full screen. */
  maxHeight?: string | number;
  fullscreen?: boolean;
  /** Small label in the top-left corner (e.g. in/out marks). */
  badge?: React.ReactNode;
}

/** The black picture area: the <video>, plus buffering, error and note overlays. */
const VideoStage: React.FC<VideoStageProps> = ({
  videoUrl,
  player,
  testId,
  notes = [],
  placeholder,
  maxHeight = '58vh',
  fullscreen = false,
  badge,
}) => (
  <Box
    sx={{
      position: 'relative',
      width: '100%',
      bgcolor: studioDark.stage,
      aspectRatio: fullscreen ? 'auto' : '16 / 9',
      maxHeight: fullscreen ? 'none' : maxHeight,
      flex: fullscreen ? 1 : 'none',
      minHeight: fullscreen ? 0 : 200,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    }}
  >
    {videoUrl ? (
      // Captions are not available for creator uploads.
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        ref={player.ref}
        src={videoUrl}
        data-testid={testId}
        preload="metadata"
        playsInline
        onClick={player.toggle}
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', cursor: 'pointer' }}
      />
    ) : (
      placeholder
    )}

    {badge && (
      <Box sx={{ position: 'absolute', top: 10, left: 10, pointerEvents: 'none' }}>{badge}</Box>
    )}

    {videoUrl && player.buffering && player.playing && !player.error && (
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <CircularProgress size={34} sx={{ color: studioDark.textMuted }} aria-label="Buffering" />
      </Box>
    )}

    {videoUrl && player.error && (
      <Box
        role="alert"
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          bgcolor: studioDark.scrim,
          color: studioDark.text,
          textAlign: 'center',
          px: 3,
        }}
      >
        <ErrorOutlineIcon />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Couldn&apos;t play this video</Typography>
        <Typography variant="caption" sx={{ color: studioDark.textMuted }}>{player.error}</Typography>
      </Box>
    )}

    {notes.length > 0 && (
      <Box
        aria-live="polite"
        sx={{ position: 'absolute', left: 12, right: 12, bottom: 12, display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start', pointerEvents: 'none' }}
      >
        {notes.slice(0, 2).map((note) => (
          <Box
            key={note.id}
            sx={{
              maxWidth: 'min(560px, 100%)',
              bgcolor: studioDark.overlay,
              color: studioDark.text,
              borderRadius: 2,
              px: 1.25,
              py: 0.75,
              borderLeft: `3px solid ${markOnDark(note.color)}`,
            }}
          >
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: studioDark.textMuted, fontVariantNumeric: 'tabular-nums' }}>
              {note.label}
            </Typography>
            <Typography sx={{ fontSize: 13.5, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {note.text}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

export default VideoStage;

/** Full screen for a container (video + transport), tracking Esc exits. */
export function useFullscreen<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(ref.current) && document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggle = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => undefined);
      return;
    }
    const el = ref.current;
    if (el && typeof el.requestFullscreen === 'function') {
      el.requestFullscreen().catch(() => undefined);
    }
  }, []);

  return { ref, isFullscreen, toggle };
}
